import { colorTuple, TButtonColorType, ThemeColor } from '@shared/types';
import {
	DEFAULT_THEME_PRIMARY_COLOR,
	DEFAULT_THEME_TERTIARY_COLOR,
} from '@shared/styles/colors';
export class ThemingUtility {
	private static _currentTheme: ThemeColor | null = null;

	static get currentTheme() {
		return {
			primary: ThemingUtility._currentTheme?.primary ?? '',
			secondary: ThemingUtility._currentTheme?.secondary ?? '',
			tertiary: ThemingUtility._currentTheme?.tertiary ?? '',
			primaryLight: ThemingUtility._currentTheme?.primaryLight ?? '',
			labelDark: ThemingUtility._currentTheme?.labelDark ?? '',
			primaryLabel: ThemingUtility._currentTheme?.primaryLabel ?? '',
		};
	}

	static set currentTheme(colorTheme: ThemeColor) {
		ThemingUtility._currentTheme = colorTheme;
	}

	static _convertRgbToHsl(rgb: colorTuple): colorTuple {
		let [red, green, blue] = rgb;
		red /= 255;
		green /= 255;
		blue /= 255;

		const maxChannel = Math.max(red, green, blue);
		const minChannel = Math.min(red, green, blue);
		const lightness = (maxChannel + minChannel) * 50;

		if (maxChannel === minChannel) {
			return [0, 0, Math.round(lightness)];
		}

		const delta = maxChannel - minChannel;
		const saturation =
			100 *
			(lightness > 50
				? delta / (2 - maxChannel - minChannel)
				: delta / (maxChannel + minChannel));

		let hue: number;
		if (maxChannel === blue) {
			hue = (red - green) / delta + 4;
		} else if (maxChannel === green) {
			hue = (blue - red) / delta + 2;
		} else {
			hue = (green - blue) / delta + (green < blue ? 6 : 0);
		}
		hue *= 60;

		return [Math.round(hue), Math.round(saturation), Math.round(lightness)];
	}

	static _convertHexToHsl(hexColor: string): colorTuple {
		let sanitizedHex = hexColor.replace(/^#/, '');

		if (sanitizedHex.length === 3) {
			sanitizedHex = sanitizedHex
				.split('')
				.map((char) => char + char)
				.join('');
		}

		const red = parseInt(sanitizedHex.substring(0, 2), 16);
		const green = parseInt(sanitizedHex.substring(2, 4), 16);
		const blue = parseInt(sanitizedHex.substring(4, 6), 16);

		return ThemingUtility._convertRgbToHsl([red, green, blue]);
	}

	static _clampHSL(
		currentHsl: colorTuple,
		saturationOffset: number,
		lightnessOffset: number,
	): colorTuple {
		return [currentHsl[0], saturationOffset, lightnessOffset];
	}

	static _convertHslToHex(hslColor: colorTuple): string {
		const [hue, saturation, lightness] = hslColor;
		const normalizedSaturation = saturation / 100;
		const normalizedLightness = lightness / 100;

		const chroma =
			(1 - Math.abs(2 * normalizedLightness - 1)) * normalizedSaturation;
		const secondaryComponent = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
		const lightnessAdjustment = normalizedLightness - chroma / 2;

		let red: number, green: number, blue: number;

		if (hue < 60) [red, green, blue] = [chroma, secondaryComponent, 0];
		else if (hue < 120) [red, green, blue] = [secondaryComponent, chroma, 0];
		else if (hue < 180) [red, green, blue] = [0, chroma, secondaryComponent];
		else if (hue < 240) [red, green, blue] = [0, secondaryComponent, chroma];
		else if (hue < 300) [red, green, blue] = [secondaryComponent, 0, chroma];
		else [red, green, blue] = [chroma, 0, secondaryComponent];

		const convertToHex = (color: number) =>
			Math.round((color + lightnessAdjustment) * 255)
				.toString(16)
				.padStart(2, '0');

		return `#${convertToHex(red)}${convertToHex(green)}${convertToHex(blue)}`;
	}

	static _parseColor(color: string): [number, number, number] {
		let hex = color.slice(1);
		if (hex.length === 3) {
			hex = hex
				.split('')
				.map((c) => c + c)
				.join('');
		}
		const intVal = parseInt(hex, 16);
		return [(intVal >> 16) & 255, (intVal >> 8) & 255, intVal & 255];
	}

	static _luminance([r, g, b]: [number, number, number]): number {
		const srgb = [r, g, b].map((v) => {
			const normalized = v / 255;
			return normalized <= 0.03928
				? normalized / 12.92
				: Math.pow((normalized + 0.055) / 1.055, 2.4);
		});
		return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
	}

	static _checkContrastRatio(firstColor: string, secondColor: string = '#fff') {
		const l1 = ThemingUtility._luminance(
			ThemingUtility._parseColor(firstColor),
		);
		const l2 = ThemingUtility._luminance(
			ThemingUtility._parseColor(secondColor),
		);
		const lighter = Math.max(l1, l2);
		const darker = Math.min(l1, l2);
		return (lighter + 0.05) / (darker + 0.05);
	}

	static _buildTheme(primaryColor: string, buttonColorType?: TButtonColorType) {
		const primaryHsl = ThemingUtility._convertHexToHsl(primaryColor);
		const secondaryHsl = ThemingUtility._clampHSL(
			primaryHsl,
			primaryHsl[1],
			90,
		);
		const tertiaryHsl = ThemingUtility._clampHSL(primaryHsl, primaryHsl[1], 95);
		const labelDarkHsl = ThemingUtility._clampHSL(
			primaryHsl,
			primaryHsl[1],
			10,
		);
		const labelDarkHex = ThemingUtility._convertHslToHex(labelDarkHsl);

		const getPrimaryButtonLabelColor = () => {
			if (buttonColorType) {
				return buttonColorType === TButtonColorType.LIGHT
					? '#fff'
					: labelDarkHex;
			}

			if (ThemingUtility._checkContrastRatio(primaryColor) < 4.5) {
				return labelDarkHex;
			}
			return '#fff';
		};

		const isPhonePeDefault =
			primaryColor.toLowerCase() === DEFAULT_THEME_PRIMARY_COLOR ||
			primaryColor === '';

		return {
			primary: primaryColor,
			secondary: ThemingUtility._convertHslToHex(secondaryHsl),
			tertiary: isPhonePeDefault
				? DEFAULT_THEME_TERTIARY_COLOR
				: ThemingUtility._convertHslToHex(tertiaryHsl),
			primaryLight: ThemingUtility._convertHslToHex(tertiaryHsl),
			labelDark: labelDarkHex,
			primaryLabel: getPrimaryButtonLabelColor(),
		};
	}

	static _createTheme(
		primaryColor: string,
		buttonColorType?: TButtonColorType,
	): string {
		const colorThemeVars = ThemingUtility._buildTheme(
			primaryColor,
			buttonColorType,
		);
		ThemingUtility._currentTheme = colorThemeVars;

		const cssVars = Object.entries(colorThemeVars)
			.map(([key, hsl]) => `--${key}: ${hsl};`)
			.join('\n ');
		return `:root {\n  ${cssVars}\n}`;
	}

	static applyTheme(primaryColor: string, buttonColorType?: TButtonColorType) {
		const theme = ThemingUtility._createTheme(primaryColor, buttonColorType);

		const styleTag = document.createElement('style');
		styleTag.id = 'theme-style';
		styleTag.innerHTML = theme;

		document.getElementById('theme-style')?.remove();
		document.head.appendChild(styleTag);
	}
}
